'use client';

import * as React from 'react';
import { useState } from 'react';

import FiltersIcon from '@/assets/icons/account/filters.svg';
import BackIcon from '@/assets/icons/back.svg';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils/cn';

export type T_FilterOption = {
  value: string;
  label: string;
};

export type T_CheckboxOption = {
  value: string;
  label: string;
};

export interface I_FilterDropdownProps {
  options: T_FilterOption[];
  value: string;
  onValueChange: (value: string) => void;

  label?: string;
  triggerLabel?: string;
  placeholder?: string;
  className?: string;
  showIcon?: boolean;
  showSelectedValue?: boolean;

  // Multi-mode props (for filtering with checkboxes)
  mode?: 'single' | 'multi';
  checkboxOptions?: T_CheckboxOption[];
  checkboxValues?: string[];
  onCheckboxChange?: (values: string[]) => void;
  checkboxLabel?: string;
}

const FilterDropdown = ({
  options,
  value,
  onValueChange,
  label,
  triggerLabel,
  placeholder,
  className,
  showIcon = true,
  showSelectedValue = false,
  mode = 'single',
  checkboxOptions = [],
  checkboxValues = [],
  onCheckboxChange,
  checkboxLabel
}: I_FilterDropdownProps) => {
  const [open, setOpen] = useState(false);

  const toggleCheckbox = (
    checkboxValue: string,
    checked: boolean | 'indeterminate'
  ) => {
    if (!onCheckboxChange) return;

    const newValues = checked
      ? [...checkboxValues, checkboxValue]
      : checkboxValues.filter(v => v !== checkboxValue);

    onCheckboxChange(newValues);
  };

  const baseLabel = triggerLabel || placeholder || 'Select';
  const selectedOption = options.find(opt => opt.value === value);

  // For sorting: show "Sort - {selectedValue}", for filter: just "Filters"
  let displayText = baseLabel;
  if (showSelectedValue && selectedOption) {
    displayText = `${baseLabel} - ${selectedOption.label}`;
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <div className="flex w-full items-center justify-center">
          <Button
            variant="default"
            className={cn(
              'min-w-43 md:min-w-60 gap-2 rounded-full bg-filters-bg px-4 py-2 capitalize hover:bg-bg-filters-bg justify-between active:bg-accordion-brand',
              open ? 'bg-accordion-brand border-primary' : 'bg-filters-bg',
              className
            )}
          >
            <P
              className={cn('text-sm', open ? 'font-bold' : 'font-normal')}
              icon={
                showIcon ? (
                  <FiltersIcon className="h-4 w-4 text-foreground" />
                ) : undefined
              }
            >
              {baseLabel}
            </P>
            {showSelectedValue && selectedOption && (
              <div className="flex w-full items-center justify-end">
                <P className="self-end">{selectedOption.label}</P>
              </div>
            )}
            <span className="h-3 w-3 min-w-fit">
              <BackIcon
                className={cn(
                  'h-full w-full transition-transform duration-200',
                  open ? 'rotate-90' : '-rotate-90'
                )}
              />
            </span>
          </Button>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="bg-accordion-brand border-1 rounded-lg min-w-43 md:min-w-60">
        {label && (
          <DropdownMenuLabel className="text-xs text-muted-foreground font-medium">
            {label}
          </DropdownMenuLabel>
        )}
        <DropdownMenuRadioGroup value={value} onValueChange={onValueChange}>
          {options.map(option => (
            <DropdownMenuRadioItem
              key={option.value}
              className="text-sm cursor-pointer"
              onSelect={e => e.preventDefault()}
              value={option.value}
            >
              {option.label.toUpperCase()}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        {mode === 'multi' && checkboxOptions.length > 0 && (
          <>
            <DropdownMenuSeparator className="bg-border" />
            {checkboxLabel && (
              <DropdownMenuLabel className="mt-1 text-xs font-medium text-muted-foreground">
                {checkboxLabel}
              </DropdownMenuLabel>
            )}
            {checkboxOptions.map(option => (
              <DropdownMenuCheckboxItem
                key={option.value}
                className="text-sm cursor-pointer"
                checked={checkboxValues.includes(option.value)}
                onCheckedChange={checked =>
                  toggleCheckbox(option.value, checked)
                }
                onSelect={e => e.preventDefault()}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FilterDropdown;
